import React, { useEffect, useState } from 'react';
import { Container, TextField, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, MenuItem, Select, FormControl, InputLabel, Grid, Chip } from '@mui/material';

const STORAGE_KEY = 'tablaAcumuladoOperario_data';

const TablaUltimoAcumuladoOperario = () => {
    const [data, setData] = useState([]);
    const [operarios, setOperarios] = useState([]);
    const [filters, setFilters] = useState({
        operario: '',
        fechaInicio: '',
        fechaFin: ''
    });

    // Cargar datos guardados de localStorage al montar
    useEffect(() => {
        try {
            const savedData = localStorage.getItem(STORAGE_KEY);
            if (savedData) {
                const parsed = JSON.parse(savedData);
                console.log('Datos cargados de localStorage:', parsed);
                setData(parsed.data || []);
                setFilters(parsed.filters || { operario: '', fechaInicio: '', fechaFin: '' });
            }
        } catch (error) {
            console.error('Error al cargar datos de localStorage:', error);
        }
    }, []);

    // Cargar lista de operarios para el desplegable
    useEffect(() => {
        const fetchOperarios = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch('/api/operators', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                if (!response.ok) {
                    console.error('Error al cargar operarios:', response.status);
                    return;
                }
                
                const result = await response.json();
                console.log('Operarios cargados:', result);
                setOperarios(Array.isArray(result) ? result : []);
            } catch (error) {
                console.error('Error al cargar los operarios:', error);
            }
        };

        fetchOperarios();
    }, []);

    // Cargar datos filtrados
    const fetchData = async () => {
        // Validar que los filtros requeridos estén completos
        if (!filters.operario || !filters.fechaInicio || !filters.fechaFin) {
            console.log('Por favor completa todos los filtros requeridos');
            return;
        }

        try {
            const queryParams = new URLSearchParams(filters).toString();
            const response = await fetch(`/api/ultimo-acumulado-operarios?${queryParams}`);
            const result = await response.json();

            if (Array.isArray(result)) {
                // Ordenar por número de máquina y luego por referencia
                const sortedData = result.sort((a, b) => {
                    const numA = parseInt(a.maquina.toLowerCase().replace(/[^0-9]/g, '')) || 0;
                    const numB = parseInt(b.maquina.toLowerCase().replace(/[^0-9]/g, '')) || 0;
                    if (numA !== numB) return numA - numB;
                    return (a.referencia || '').localeCompare(b.referencia || '');
                });

                setData(sortedData);

                // Guardar datos y filtros en localStorage
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ data: sortedData, filters }));
            } else {
                console.error('La respuesta no es un array:', result);
                setData([]);
            }
        } catch (error) {
            console.error('Error al cargar los datos de operarios:', error);
            setData([]);
        }
    };

    // Manejar cambios en los filtros
    const handleFilterChange = (field, value) => {
        setFilters({ ...filters, [field]: value });
    };

    const formatNum = (v) => Math.round(Number(v) || 0).toLocaleString('es-ES');

    const totalRevs  = data.reduce((s, d) => s + (Number(d.revoluciones) || 0), 0);
    const totalMetros = data.reduce((s, d) => s + (Number(d.metros) || 0), 0);

    return (
        <Container>
            <Typography variant="h4" align="center" mb={9} gutterBottom>
                Último Acumulado por Operario
            </Typography>
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl variant="outlined" fullWidth>
                        <InputLabel id="operario-label">Operario</InputLabel>
                        <Select
                            labelId="operario-label"
                            value={filters.operario}
                            onChange={(e) => handleFilterChange('operario', e.target.value)}
                            label="Operario"
                            displayEmpty
                            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                        >
                            <MenuItem value="" disabled>
                                <em>Seleccionar operario</em>
                            </MenuItem>
                            {(Array.isArray(operarios) ? operarios : []).map((op) => (
                                <MenuItem key={op._id} value={op._id}>
                                    {op.name && op.lastName ? `${op.name} ${op.lastName}` : op.name || op.email || 'Sin nombre'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        label="Fecha inicial"
                        type="date"
                        value={filters.fechaInicio}
                        onChange={(e) => handleFilterChange('fechaInicio', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{
                            '& .MuiInputBase-root': {
                                backgroundColor: '#302f2fff'
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        label="Fecha final"
                        type="date"
                        value={filters.fechaFin}
                        onChange={(e) => handleFilterChange('fechaFin', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{
                            '& .MuiInputBase-root': {
                                backgroundColor: '#302f2fff'
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Button variant="contained" color="primary" onClick={fetchData} fullWidth sx={{ height: '56px' }}>
                        Filtrar
                    </Button>
                </Grid>
            </Grid>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#071525' }}>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Máquina</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Referencia</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="right">Revoluciones</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="right">Metros producidos</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!Array.isArray(data) || data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No hay datos para los filtros seleccionados.
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {data.map((item, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>{item.maquina}</TableCell>
                                        <TableCell>
                                            {item.referencia && item.referencia !== 'Sin referencia'
                                                ? <Chip label={item.referencia} size="small" color="primary" variant="outlined" />
                                                : <Typography variant="caption" color="text.secondary">Sin referencia</Typography>
                                            }
                                        </TableCell>
                                        <TableCell align="right">{formatNum(item.revoluciones)}</TableCell>
                                        <TableCell align="right">
                                            {item.metros > 0
                                                ? <strong>{formatNum(item.metros)} m</strong>
                                                : <Typography variant="caption" color="text.secondary">—</Typography>
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {/* Fila totales */}
                                <TableRow sx={{ backgroundColor: '#071525' }}>
                                    <TableCell colSpan={2} sx={{ color: '#fff', fontWeight: 'bold' }}>TOTAL</TableCell>
                                    <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{formatNum(totalRevs)}</TableCell>
                                    <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{totalMetros > 0 ? `${formatNum(totalMetros)} m` : '—'}</TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    );
};

export default TablaUltimoAcumuladoOperario;